import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  return (
    <section id="ueber" className="py-24 bg-stone-50 dark:bg-navy-950 overflow-hidden transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Überschrift über volle Breite */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-6 leading-tight">
            Lyrik, die klingt – Song-Konzepte aus zeitloser Poesie
          </h2>
          <p className="text-2xl font-serif italic text-amber-800 dark:text-amber-400">
            Anregung, nicht Perfektion
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-6 text-stone-700 dark:text-stone-300 leading-relaxed text-lg">
              <p className="font-bold text-stone-900 dark:text-stone-100">
              Am Anfang steht das Wort. Nichts ist so spezifisch menschlich, so unmöglich außerhalb menschlichen Denkvermögens reproduzierbar, wie Poesie.
              Andere, informationsorientierte sprachliche Äußerungen finden wir durchaus im Tierreich und in der „Künstlichen Intelligenz",
               die ja im Grunde genommen nicht sonderlich intelligent ist, wenn man sich außerhalb digitaler Informationsströme begibt, 
               also bei allem, was nicht auf eine 1/0-Dichotomie zurückgeführt werden kann. 
              </p>
              <p>
              KI kann Lyrik simulieren. Aber genau da zeigt sie ihre Grenze. Diese papageienartigen Versuche sind auch für den Laien leicht als solche erkennbar. 
              Im Gegensatz zu Fachtexten, die bei richtigem Prompten sehr „human“ daherkommen können; oder auch der Musikerstellung.
               Die KI wird da bestimmt nicht im eigentlichen, sehr tiefen Sinn, innovativ sein können, aber der Musikbetrieb ist so gesehen zu 99,9% ja auch nicht. 
              </p>
              <p>
                Und da rede ich nicht nur vom Dudel-Funk mit dem bestem aus diversen Jahrzehnten. 
                Die KI kann, wie die meisten Musiker und Produzenten, das vorhandene Wissen und Können facettenreich und auch voll überraschender Momente anwenden. 
                Sie kann damit „richtigen“ Musikern gut zuarbeiten, wird sie aber nie ersetzen können.
              </p>
              <p>
                Und genau dieses Zuarbeiten ist das, was auf dieser Website, mit diesem Projekt, passiert. 
                Liedzeit entwirft Song-Konzepte auf der Basis von aktuellen und zeitlosen Qualitätstexten. 
                Die Songs werden mit KI und DAW produziert. Sie sollen Anregungen sein, keine fertigen Produkte. 
                Liedzeit versteht sich als kreativer Ausgangspunkt – ein Raum, in dem Lyrik in Klang übersetzt wird, und wo Interessierte eigene Wege weitergehen können. 
                Nicht mehr, aber auch nicht weniger. 
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-full min-h-[400px]"
          >
            <div className="h-full rounded-sm overflow-hidden shadow-2xl">
              <img
                src="/images/about-music.svg"
                alt="Lyrik und Musik"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-16 -left-16 w-64 h-64 hidden md:block">
              <img
                src="/logo_liedzeit_bordeaux.png"
                alt="Liedzeit Logo"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24">
          {[
            {
              title: 'Zeitlose Texte',
              text: 'Ob Heinrich Heine, Hermann Hesse oder Sören Padel – Liedzeit arbeitet mit Texten, die über den Moment hinausgehen. Lyrik, die etwas zu sagen hat, wird zur Grundlage guter Musik.'
            },
            {
              title: 'KI & DAW',
              text: 'Liedzeit nutzt aktuelle KI-Werkzeuge und digitale Audio-Workstations, um aus Lyrik musikalische Konzepte zu formen. Das Ergebnis ist nicht zwingend ein Endprodukt, sondern ein Anfang.'
            },
            {
              title: 'Song-Konzepte',
              text: 'Jede Komposition ist ein Vorschlag, keine Festlegung. Liedzeit lädt ein, weiterzudenken, neu zu interpretieren und die Texte in eigene musikalische Sprache zu übersetzen.'
            }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-stone-100 border-b border-amber-200 dark:border-amber-800 pb-2">
                {item.title}
              </h3>
              <p className="text-stone-600 dark:text-stone-400">{item.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-brand dark:bg-navy-800 text-white p-12 md:p-20 text-center relative overflow-hidden rounded-sm"
        >
          <div className="absolute inset-0 opacity-20">
            <img src="/images/hero-music.svg" alt="Texture" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h4 className="text-2xl md:text-3xl font-serif italic mb-8 leading-relaxed">
              „Poesie ist ein scheuer Blick in die Unendlichkeit."
            </h4>
            <div className="w-16 h-1 bg-amber-600 dark:bg-amber-400 mx-auto"></div>
            <p className="mt-6 text-stone-300 text-sm">Sören Padel, Liedzeit</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
